import React from "react";
import {
  Menu as MuiMenu,
  MenuItem,
  Typography,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Avatar,
  Skeleton,
} from "@mui/material";
import { CheckCircle, Business } from "@mui/icons-material";

import { useAuth } from "../../context/AuthContext";

export function BranchSwitchMenu({
  anchorEl,
  openMenu,
  handleCloseMenu,
  handleCloseCompanies,
  branches = [],
  companies = [],
  onSelectBranch,
  handleSwitchCompany,
  loading = false,
  title = "Cambiar sucursal",
}) {
  const { currentBranch } = useAuth();
  const list = branches.length > 0 ? branches : companies;
  const onClose = handleCloseMenu || handleCloseCompanies || (() => {});
  const onSelect = onSelectBranch || handleSwitchCompany || (() => {});
  const currentBranchId = String(currentBranch?.id || "");

  const getInitials = (name) =>
    (name || "")
      .split(" ")
      .map((s) => s?.[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "S";

  return (
    <MuiMenu
      anchorEl={anchorEl}
      open={Boolean(openMenu)}
      onClose={onClose}
      PaperProps={{
        sx: {
          minWidth: 260,
          maxWidth: 320,
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.divider}`,
          boxShadow: "0 18px 50px rgba(16,24,40,0.12)",
          p: 1,
          overflow: "hidden",
        },
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Box
        sx={{
          height: 4,
          mx: -1,
          mt: -1,
          mb: 1,
          background: (t) =>
            `linear-gradient(90deg, ${t.palette.secondary.main}, ${t.palette.primary.main})`,
        }}
      />

      <Box sx={{ px: 2, py: 1.25 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
      </Box>
      <Divider sx={{ mb: 1 }} />

      {loading &&
        [1, 2, 3].map((idx) => (
          <MenuItem key={idx} sx={{ borderRadius: 1.5, mb: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Skeleton variant="circular" width={24} height={24} />
            </ListItemIcon>
            <ListItemText
              primary={<Skeleton variant="text" width={160} sx={{ fontSize: "0.9rem" }} />}
            />
            <Skeleton variant="circular" width={18} height={18} />
          </MenuItem>
        ))}

      {!loading && list.length === 0 && (
        <MenuItem disabled>
          <ListItemIcon>
            <Business fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="(Sin sucursales disponibles)" />
        </MenuItem>
      )}

      {!loading &&
        list.map((item) => {
          const itemId = String(item?.id || "");
          const isCurrent = itemId === currentBranchId;
          const displayName = item?.label || item?.alias || item?.name || "Sucursal";

          return (
            <MenuItem
              key={item?.id}
              selected={isCurrent}
              onClick={() => {
                if (!isCurrent) {
                  onSelect(item?.id);
                }
                onClose();
              }}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                ...(isCurrent && {
                  backgroundColor: "rgba(243,105,77,0.10)",
                  "&:hover": { backgroundColor: "rgba(243,105,77,0.14)" },
                }),
                "&:hover": { backgroundColor: "rgba(0,0,0,0.03)" },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Avatar
                  sx={{
                    width: 26,
                    height: 26,
                    bgcolor: isCurrent ? "secondary.main" : "rgba(0,0,0,0.18)",
                    color: isCurrent ? "secondary.contrastText" : "rgba(0,0,0,0.72)",
                    fontSize: 12,
                    fontWeight: 900,
                  }}
                >
                  {getInitials(displayName)}
                </Avatar>
              </ListItemIcon>

              <ListItemText
                primary={
                  <Typography sx={{ fontWeight: isCurrent ? 900 : 600 }} noWrap>
                    {displayName}
                  </Typography>
                }
              />

              {isCurrent && <CheckCircle fontSize="small" color="secondary" />}
            </MenuItem>
          );
        })}
    </MuiMenu>
  );
}

export default BranchSwitchMenu;
